// janus-engine/zones/zone-geometry.ts — Lane MV2 S1 (2026-09-28): moving / resizing an already-placed persistent zone,
// resolved in the browser. The PHP twin is includes/functions/janus/encounter/zone_geometry_truth.php (both lifted verbatim
// from the legacy endpoint options/tools/statuses/ft3d/ft3d_encounter_status_effect_move.php); golden vectors:
// tools/probes/zone_truth_geometry_vectors.php → tools/test/fixtures/zone-truth-geometry-vectors.json, replayed by
// tools/test/janus-zone-truth-geometry.test.mjs.
//
// A move writes the span columns + the re-derived stored radius and nothing else: legacy never re-ran zone membership on a
// drag (no occupant is added or stripped until someone moves or a turn ends), so this resolver does not either.

export const ZONE_GEOMETRY_SCHEMA = 'janus.status.geometry.truth.v1';

/** The six span coordinates (feet; x / y floor plane, z elevation), origin → distance point. */
export interface ZoneSpan { ox: number; oy: number; oz: number; ex: number; ey: number; ez: number }
export type ZoneTargetKind = 'encounter' | 'encounter_creature' | 'inventory';

export interface ZoneMoveInput {
  /** The anchor row's stored span and radius, as the page holds it (lean rows drop zeros: missing means 0). */
  before: ZoneSpan & { distance: number };
  /** The new span the GM drew. `applyY` = both y's were supplied (FT3D always supplies them). */
  after: ZoneSpan;
  applyY: boolean;
  /** status_aoe_type uuid (null / '' → the stored radius is kept) and its variable_name (lower-case). */
  aoeTypeId: string | null;
  aoeVar: string;
  /** The zone's definition carries a truthy aoe_follows_target. */
  followsTarget: boolean;
  /** What the anchor's status_target_id names. */
  targetKind: ZoneTargetKind;
}
export type ZoneMoveVerdict =
  | { kind: 'unchanged' }
  | { kind: 'refused'; message: string }
  | { kind: 'move'; ftMoved: number; aoeDistance: number; applyY: boolean };

/** PHP round($x, 1): half away from zero at one decimal. */
export function phpRound1(value: number): number {
  const scaled = Math.abs(value) * 10;
  const rounded = Math.floor(scaled + 0.5 + 1e-9);
  return Math.sign(value) * rounded / 10;
}
/** PHP round($x) (0 decimals), half away from zero. */
function phpRound0(value: number): number {
  return Math.sign(value) * Math.floor(Math.abs(value) + 0.5 + 1e-12);
}

/** ft_zone_span_aoe_distance(). */
export function zoneSpanAoeDistance(aoeTypeId: string | null, varName: string, span: ZoneSpan, applyY: boolean, existing: number): number {
  if (aoeTypeId === null || aoeTypeId === '') return existing;
  const dy = applyY ? span.ey - span.oy : 0;
  const raw = Math.sqrt((span.ex - span.ox) ** 2 + dy * dy + (span.ez - span.oz) ** 2);
  if (raw <= 0.01) return existing;
  const v = (varName === '' ? 'cube' : varName).toLowerCase();
  if (['sphere', 'circle', 'cylinder'].includes(v)) return phpRound1(raw / 2);
  if (['cube', 'square', 'line', 'cone'].includes(v)) return phpRound1(raw);
  return phpRound1(raw / 2);
}

/** ft_zone_center_moved_ft(). */
export function zoneCenterMovedFt(before: ZoneSpan, after: ZoneSpan, applyY: boolean): number {
  const dx = (after.ox + after.ex) / 2 - (before.ox + before.ex) / 2;
  const dz = (after.oz + after.ez) / 2 - (before.oz + before.ez) / 2;
  const dy = applyY ? (after.oy + after.ey) / 2 - (before.oy + before.ey) / 2 : 0;
  return phpRound0(Math.sqrt(dx * dx + dy * dy + dz * dz));
}

/** ft_zone_span_unchanged(). */
export function zoneSpanUnchanged(before: ZoneSpan, after: ZoneSpan, applyY: boolean): boolean {
  const keys: (keyof ZoneSpan)[] = applyY ? ['ox', 'oz', 'ex', 'ez', 'oy', 'ey'] : ['ox', 'oz', 'ex', 'ez'];
  return keys.every(k => Math.abs(before[k] - after[k]) < 0.001);
}

export const PINNED_ZONE_MESSAGE = 'This effect is pinned to a token; move the token or resize the zone — you cannot drag the zone center.';
/** ft_zone_pinned_refusal(). */
export function zonePinnedRefusal(followsTarget: boolean, ftMoved: number, varName: string, targetKind: ZoneTargetKind): string | null {
  if (!followsTarget || ftMoved <= 0) return null;
  if (['line', 'cone'].includes(varName.toLowerCase())) return null;
  if (targetKind !== 'encounter_creature' && targetKind !== 'inventory') return null;
  return PINNED_ZONE_MESSAGE;
}

/** The whole legacy decision, in the endpoint's order: no-op → pinned refusal → re-derived radius. */
export function resolveZoneMove(input: ZoneMoveInput): ZoneMoveVerdict {
  const span = (s: ZoneSpan): boolean => [s.ox, s.oy, s.oz, s.ex, s.ey, s.ez].every(Number.isFinite);
  if (!span(input.before) || !span(input.after) || !Number.isFinite(input.before.distance)) return { kind: 'refused', message: 'Invalid zone geometry.' };
  if ([input.after.ox, input.after.oy, input.after.oz, input.after.ex, input.after.ey, input.after.ez].some(v => Math.abs(v) > 100000))
    return { kind: 'refused', message: 'Invalid zone geometry.' };
  if (zoneSpanUnchanged(input.before, input.after, input.applyY)) return { kind: 'unchanged' };
  const ftMoved = zoneCenterMovedFt(input.before, input.after, input.applyY);
  const refusal = zonePinnedRefusal(input.followsTarget, ftMoved, input.aoeVar, input.targetKind);
  if (refusal) return { kind: 'refused', message: refusal };
  return { kind: 'move', ftMoved, aoeDistance: zoneSpanAoeDistance(input.aoeTypeId, input.aoeVar, input.after, input.applyY, input.before.distance), applyY: input.applyY };
}
